import { useCallback, useEffect, useRef } from 'react';
import type { ChatAction, Message, MessageReaction, SessionConfig } from '../types';
import { threadCapabilityHeaders } from '../utils/threadCapability';

/** Rapid thumb toggles on one message collapse into a single request; the last one wins. */
export const FEEDBACK_DEBOUNCE_MS = 300;
/** Server contract: `comment` is rejected above this length. */
export const FEEDBACK_COMMENT_MAX_LENGTH = 500;

export interface UseMessageFeedbackOptions {
  threadId: string | null | undefined;
  apiBaseUrl?: string | null;
  /** Explicit override; otherwise `<apiBaseUrl>/response/feedback`. */
  feedbackEndpoint?: string | null;
  /** Current message list; used to look up `localOnly` and the pre-toggle reaction. */
  messages: Message[];
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>;
  sessionConfig?: SessionConfig;
  /**
   * Local -> server id for messages a thread refresh matched
   * (`useThread().resolveServerMessageId`); returns the id itself when unmapped.
   */
  resolveServerMessageId?: (localId: string) => string;
  dispatch: (action: ChatAction) => void;
  trackAnalytics: (action: string, data?: Record<string, unknown>) => void;
  fetchImpl?: typeof fetch;
}

export interface MessageFeedbackApi {
  /**
   * Optimistically records `reaction` on the message and (debounced) POSTs it to the
   * feedback endpoint. `null` clears; `comment` is only meaningful with a reaction.
   */
  setReaction: (messageId: string, reaction: MessageReaction, comment?: string) => void;
}

interface PendingFeedback {
  timer: ReturnType<typeof setTimeout>;
  /** What the message showed before the first toggle in this debounce window (revert target). */
  prevReaction: MessageReaction;
  prevComment: string | undefined;
  reaction: MessageReaction;
  comment: string | undefined;
}

export function resolveFeedbackEndpoint(
  feedbackEndpoint?: string | null,
  apiBaseUrl?: string | null
): string | null {
  if (feedbackEndpoint) return feedbackEndpoint;
  if (apiBaseUrl) return `${apiBaseUrl.replace(/\/$/, '')}/response/feedback`;
  return null;
}

function normalizeComment(reaction: MessageReaction, comment?: string): string | undefined {
  if (reaction === null || typeof comment !== 'string') return undefined;
  const trimmed = comment.trim().slice(0, FEEDBACK_COMMENT_MAX_LENGTH);
  return trimmed || undefined;
}

/**
 * Id the server stored `message` under, best available first: the `serverId`
 * the chat reply carried (JSON `messageId` / SSE `message_id`), else the id a
 * thread refresh mapped it to, else the local id. The local-id fallback only
 * happens against servers that do not return the stored id yet; it is the
 * pre-contract behaviour (the server may 404 and the reaction reverts).
 */
export function serverMessageIdFor(
  message: Pick<Message, 'id' | 'serverId'>,
  resolveServerMessageId?: (localId: string) => string
): string {
  return message.serverId ?? resolveServerMessageId?.(message.id) ?? message.id;
}

/**
 * Per-message thumbs up/down feedback: optimistic reducer update, debounced
 * fire-and-forget POST, revert + `message_feedback_failed` analytics on failure.
 * Messages without a thread and `localOnly` messages are only updated locally.
 */
export function useMessageFeedback(options: UseMessageFeedbackOptions): MessageFeedbackApi {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const pendingRef = useRef(new Map<string, PendingFeedback>());
  // Latest request sequence per message; a failure only reverts when nothing newer was sent.
  const sendSeqRef = useRef(new Map<string, number>());
  const mountedRef = useRef(true);

  const send = useCallback((messageId: string, pending: PendingFeedback) => {
    const {
      threadId,
      apiBaseUrl,
      feedbackEndpoint,
      messages,
      withAuthHeaders,
      sessionConfig,
      resolveServerMessageId,
      dispatch,
      trackAnalytics,
      fetchImpl,
    } = optionsRef.current;
    const endpoint = resolveFeedbackEndpoint(feedbackEndpoint, apiBaseUrl);
    const message = messages.find(m => m.id === messageId);
    if (!threadId || !endpoint || !message || message.localOnly) return;
    const doFetch = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
    if (!doFetch) return;

    const seq = (sendSeqRef.current.get(messageId) ?? 0) + 1;
    sendSeqRef.current.set(messageId, seq);

    const { reaction, comment } = pending;
    const body: Record<string, unknown> = {
      threadId,
      messageId: serverMessageIdFor(message, resolveServerMessageId),
      reaction,
    };
    if (comment) body.comment = comment;

    const onFailure = (status: number | undefined, error: string) => {
      if (!mountedRef.current) return;
      const superseded =
        sendSeqRef.current.get(messageId) !== seq || pendingRef.current.has(messageId);
      if (!superseded) {
        dispatch({
          type: 'SET_MESSAGE_REACTION',
          id: messageId,
          reaction: pending.prevReaction,
          comment: pending.prevComment,
        });
      }
      trackAnalytics('message_feedback_failed', {
        messageId,
        reaction,
        threadId,
        ...(status !== undefined ? { status } : {}),
        error,
      });
    };

    let request: Promise<Response>;
    try {
      request = doFetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: withAuthHeaders(threadCapabilityHeaders(threadId, sessionConfig)),
        body: JSON.stringify(body),
      });
    } catch (err) {
      request = Promise.reject(err);
    }
    Promise.resolve(request).then(
      res => {
        if (!res.ok) onFailure(res.status, `http_${res.status}`);
      },
      err =>
        onFailure(undefined, err instanceof Error && err.message ? err.message : 'network_error')
    );
  }, []);

  const flush = useCallback(
    (messageId: string) => {
      const pending = pendingRef.current.get(messageId);
      if (!pending) return;
      pendingRef.current.delete(messageId);
      send(messageId, pending);
    },
    [send]
  );

  useEffect(() => {
    mountedRef.current = true;
    const pendingByMessage = pendingRef.current;
    return () => {
      mountedRef.current = false;
      // Do not lose feedback the visitor gave right before closing the widget.
      pendingByMessage.forEach((pending, messageId) => {
        clearTimeout(pending.timer);
        send(messageId, pending);
      });
      pendingByMessage.clear();
    };
  }, [send]);

  const setReaction = useCallback(
    (messageId: string, reaction: MessageReaction, comment?: string) => {
      const { messages, dispatch, trackAnalytics, threadId } = optionsRef.current;
      const message = messages.find(m => m.id === messageId);
      if (!message) return;
      const normalizedComment = normalizeComment(reaction, comment);

      dispatch({
        type: 'SET_MESSAGE_REACTION',
        id: messageId,
        reaction,
        comment: normalizedComment,
      });
      trackAnalytics('message_reaction', { messageId, reaction, threadId: threadId ?? null });

      if (!threadId || message.localOnly) return;

      const existing = pendingRef.current.get(messageId);
      if (existing) clearTimeout(existing.timer);
      pendingRef.current.set(messageId, {
        prevReaction: existing ? existing.prevReaction : (message.reaction ?? null),
        prevComment: existing ? existing.prevComment : message.feedbackComment,
        reaction,
        comment: normalizedComment,
        timer: setTimeout(() => flush(messageId), FEEDBACK_DEBOUNCE_MS),
      });
    },
    [flush]
  );

  return { setReaction };
}
